import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import {
  Phone, Briefcase, Calendar, MapPin, BadgeCheck, Upload, FileText, PenLine,
  ShieldCheck, DollarSign, Box, Clock, CheckCircle2, AlertCircle, X, Loader2, Send, Printer,
  Receipt, ChevronDown, ChevronRight, Lock, Pencil, Languages, Save, Eraser, Camera, Landmark, IdCard, AlertTriangle,
} from 'lucide-react';
import DataSheetViewer from '@/components/personnel/DataSheetViewer';
import PersonnelActions from '@/components/personnel/PersonnelActions';
import PropertyRegisterSection from '@/components/personnel/PropertyRegisterSection';
import PersonnelIntake from '@/components/personnel/PersonnelIntake';
import DocumentCapture from '@/components/personnel/DocumentCapture';
import IdentityDocuments from '@/components/personnel/IdentityDocuments';
import { GovFormsPanel } from '@/components/personnel/GovFormsPanel';
import PersonnelPhotoField from '@/components/admin/PersonnelPhotoField';
import { DIR_FONT } from '@/components/personnel/PersonnelDirectoryCard';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const fmtDate = (v) => {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : String(v);
};
const fmtPhone = (v) => {
  if (!v) return '—';
  let d = String(v).replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(v);
};
const TRAINING_LABELS = {
  safety_training: 'Safety Training',
  customer_first_training: 'Customer First Training',
  tfs_standard_cleaning_training: 'TFS Standard Cleaning Training',
  company_rules_acknowledgment: 'Company Guidelines Training',
};
const STATUS_TONE = {  Verified: 'text-emerald-700 bg-emerald-50', Signed: 'text-emerald-700 bg-emerald-50',
  Complete: 'text-emerald-700 bg-emerald-50', Completed: 'text-emerald-700 bg-emerald-50', Authorized: 'text-emerald-700 bg-emerald-50',
  Active: 'text-emerald-700 bg-emerald-50', Clear: 'text-emerald-700 bg-emerald-50',
  'On File': 'text-emerald-700 bg-emerald-50',
  Uploaded: 'text-blue-700 bg-blue-50', 'Pending Review': 'text-amber-700 bg-amber-50',
  'Pending Verification': 'text-amber-700 bg-amber-50', 'Pending Completion': 'text-amber-700 bg-amber-50',
  Pending: 'text-amber-700 bg-amber-50', Submitted: 'text-amber-700 bg-amber-50',
  'Pending Approval': 'text-amber-700 bg-amber-50',
  Draft: 'text-slate-600 bg-slate-100', 'Returned for Correction': 'text-amber-700 bg-amber-50',
  Approved: 'text-blue-700 bg-blue-50', Inactive: 'text-slate-500 bg-slate-100', Terminated: 'text-rose-600 bg-rose-50',
  Missing: 'text-rose-600 bg-rose-50', Rejected: 'text-rose-600 bg-rose-50', Expired: 'text-rose-600 bg-rose-50',
  'Not Applicable': 'text-slate-400 bg-slate-100', 'Not Required': 'text-slate-400 bg-slate-100',
};
const Pill = ({ s }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE[s] || 'text-slate-500 bg-slate-100'}`} data-testid="pc-status-pill">{s || '—'}</span>;

const Section = ({ icon: Icon, title, children, action, className = '' }) => (
  <div className={`rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${className}`} data-testid={`pc-section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="mb-3 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm font-bold text-[#0E2A47]"><Icon size={16} className="text-[#1763B6]" />{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const TrainingEditor = ({ card, token, onDone }) => {
  const ts = card.training_status || {};
  const applicable = Object.keys(ts).filter((k) => ts[k] !== 'Not Applicable');
  const [openK, setOpenK] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const start = (k) => { const r = (card.training || {})[k] || {}; setF({ completed: !!r.completed, completion_date: r.completion_date || '', verified_by: r.verified_by || '', notes: r.notes || '' }); setOpenK(k); };
  const save = async (k) => {
    setBusy(true);
    try {
      await axios.put(`${API}/personnel/${card.id}/training/${k}`, { completed: f.completed, completion_date: f.completion_date || null, verified_by: f.verified_by || null, notes: f.notes || null }, auth(token));
      toast.success('Training updated'); setOpenK(null); await onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save training'); }
    setBusy(false);
  };
  const upload = async (k, file) => {
    if (!file) return; const fd = new FormData(); fd.append('file', file);
    try { await axios.post(`${API}/personnel/${card.id}/training/${k}/document`, fd, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Document attached'); await onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };
  return (
    <div className="space-y-1.5" data-testid="pc-training-editor">
      {applicable.map((k) => {
        const rec = (card.training || {})[k] || {};
        return (
          <div key={k} className="border-t border-slate-100 py-1.5 first:border-0" data-testid={`pc-training-row-${k}`}>
            <div className="flex items-center justify-between text-sm">
              <span>{TRAINING_LABELS[k] || k.replace(/_/g, ' ')}</span>
              <span className="flex items-center gap-2"><Pill s={ts[k] || (rec.completed ? 'Completed' : 'Missing')} />
                {card.can_manage && <button onClick={() => (openK === k ? setOpenK(null) : start(k))} data-testid={`pc-training-edit-${k}`} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-[#1763B6] hover:bg-blue-50"><Pencil size={11} />Edit</button>}
              </span>
            </div>
            {openK === k && (
              <div className="mt-2 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-2" data-testid={`pc-training-form-${k}`}>
                <label className="col-span-2 flex items-center gap-2 text-[11px] font-semibold text-slate-600"><input type="checkbox" checked={f.completed} onChange={(e) => setF((p) => ({ ...p, completed: e.target.checked }))} data-testid={`pc-training-completed-${k}`} />Completed / acknowledged</label>
                <label className="text-[10px] font-semibold uppercase text-slate-500">Completion date<input type="date" value={f.completion_date} onChange={(e) => setF((p) => ({ ...p, completion_date: e.target.value }))} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs" data-testid={`pc-training-date-${k}`} /></label>
                <label className="text-[10px] font-semibold uppercase text-slate-500">Verified by<input value={f.verified_by} onChange={(e) => setF((p) => ({ ...p, verified_by: e.target.value }))} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs" data-testid={`pc-training-verifier-${k}`} /></label>
                <label className="col-span-2 text-[10px] font-semibold uppercase text-slate-500">Notes<input value={f.notes} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs" data-testid={`pc-training-notes-${k}`} /></label>
                <div className="col-span-2 flex items-center justify-between">
                  <label className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-[#1763B6]"><Upload size={12} />{rec.document_url ? 'Replace document' : 'Attach document'}<input type="file" className="hidden" onChange={(e) => upload(k, e.target.files[0])} data-testid={`pc-training-doc-${k}`} /></label>
                  <div className="flex gap-2"><button onClick={() => setOpenK(null)} className="rounded border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-600">Cancel</button>
                    <button onClick={() => save(k)} disabled={busy} className="rounded bg-[#1763B6] px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50" data-testid={`pc-training-save-${k}`}>{busy ? 'Saving…' : 'Save'}</button></div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {applicable.length === 0 && <p className="text-xs text-slate-400">No training applicable for this role.</p>}
    </div>
  );
};

const ScreeningEditor = ({ card, token, onDone, onClose }) => {
  const s = card.screening || {};
  const isW2 = card.worker_classification === 'W-2 Employee';
  const [f, setF] = useState({
    background_status: s.background_status || 'Pending Determination', background_date: s.background_date || '',
    background_completion_date: s.background_completion_date || '', background_reviewer: s.background_reviewer || '',
    background_expiration: s.background_expiration || '',
    workers_comp_status: s.workers_comp_status || '', insurance_status: s.insurance_status || '',
    insurance_expiration: s.insurance_expiration || '', business_verification_status: s.business_verification_status || '',
    notes: s.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await axios.patch(`${API}/personnel/${card.id}/screening`, f, auth(token)); toast.success('Screening updated'); onClose(); await onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save screening'); }
    setBusy(false);
  };
  const Fld = ({ k, label, type = 'text' }) => (
    <label className="text-[10px] font-semibold uppercase text-slate-500">{label}
      <input type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal" data-testid={`pc-screen-${k}`} /></label>
  );
  return (
    <div className="space-y-3" data-testid="pc-screening-editor">
      <div className="rounded-lg bg-slate-50 p-2">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">Background screening</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] font-semibold uppercase text-slate-500">Status
            <select value={f.background_status} onChange={(e) => set('background_status', e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal" data-testid="pc-screen-background_status">
              {['Required', 'Not Required', 'Pending Determination', 'Pending', 'Clear', 'Complete', 'Rejected'].map((o) => <option key={o}>{o}</option>)}</select></label>
          {Fld({ k: 'background_reviewer', label: 'Verified by' })}
          {Fld({ k: 'background_date', label: 'Requested date', type: 'date' })}
          {Fld({ k: 'background_completion_date', label: 'Completion date', type: 'date' })}
          {Fld({ k: 'background_expiration', label: 'Expiration / renewal', type: 'date' })}
        </div>
      </div>
      {isW2 ? (
        <div className="rounded-lg bg-emerald-50 px-2 py-2 text-[11px] text-emerald-800" data-testid="pc-screen-w2-note">Workers' compensation coverage is provided by TFS for W-2 employees. Subcontractor insurance / business-registration workflows do not apply.</div>
      ) : (
        <div className="rounded-lg bg-slate-50 p-2">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">Contractor / business compliance</div>
          <div className="grid grid-cols-2 gap-2">
            {Fld({ k: 'workers_comp_status', label: "Workers' comp (coverage/exemption)" })}
            {Fld({ k: 'insurance_status', label: 'Insurance (COI)' })}
            {Fld({ k: 'insurance_expiration', label: 'Insurance expiration', type: 'date' })}
            {Fld({ k: 'business_verification_status', label: 'Business verification' })}
          </div>
        </div>
      )}
      {Fld({ k: 'notes', label: 'Notes' })}
      <div className="flex justify-end gap-2"><button onClick={onClose} className="rounded border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="pc-screen-cancel">Cancel</button>
        <button onClick={save} disabled={busy} className="rounded bg-[#1763B6] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="pc-screen-save">{busy ? 'Saving…' : 'Save'}</button></div>
    </div>
  );
};

const SECTION_DEFS = {
  main: [
    ['legal_first_name', 'Legal first name'], ['legal_middle_name', 'Middle name'], ['legal_last_name', 'Legal last name'],
    ['preferred_name', 'Preferred name'], ['suffix', 'Suffix'], ['email', 'Email'],
    ['phone', 'Cell phone'], ['home_phone', 'Home phone'], ['alt_phone', 'Alternate phone'],
    ['street_address', 'Street address'], ['address_line2', 'Apartment/Unit'], ['city', 'City'], ['state', 'State'], ['zip_code', 'ZIP code'],
  ],
  emergency: [
    ['emergency_contact_name', 'Contact'], ['emergency_contact_relationship', 'Relationship'],
    ['emergency_contact_phone', 'Telephone'], ['emergency_contact_alt_phone', 'Alternate phone'],
  ],
  identification: [
    ['gov_id_type', 'ID document type'], ['gov_id_number', 'ID document number'],
    ['gov_id_issuing_authority', 'Issuing state / authority'], ['gov_id_status', 'ID status'],
    ['work_auth_status', 'Work authorization'], ['work_auth_date', 'Work auth date (YYYY-MM-DD)'],
    ['work_auth_reverify_date', 'Reverification date (YYYY-MM-DD)'],
    ['dl_number', "Driver's license #"], ['dl_state', 'DL state'], ['dl_expiration', 'DL expiration (YYYY-MM-DD)'],
    ['authorized_company_driver', 'Authorized company driver (Yes/No/Pending)'],
    ['driver_auth_date', 'Driver auth date (YYYY-MM-DD)'], ['driver_auth_expiration', 'Driver auth expiration (YYYY-MM-DD)'],
  ],
  reporting: [
    ['direct_supervisor', 'Direct supervisor'], ['direct_supervisor_id', ''],
    ['operations_manager', 'Operations manager'], ['operations_manager_id', ''],
    ['reporting_manager', 'Reporting manager'], ['reporting_manager_id', ''],
    ['reporting_effective_date', 'Reporting effective date (YYYY-MM-DD)'],
  ],
};

const efInput = 'mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-xs font-normal text-slate-800 focus:border-[#1763B6] outline-none';
const efLbl = 'block text-[10px] font-semibold uppercase text-slate-500';
const EF = ({ form, setForm, k, label, cls = '' }) => (
  <label className={`${efLbl} ${cls}`}>{label}
    <input value={form[k] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
      className={efInput} data-testid={`pc-edit-input-${k}`} />
  </label>
);

// Compact, proportionate Main Personnel Information editor (item 5).
const MainEditForm = ({ card, effDate, setEffDate, form, setForm, onSave, onCancel, saving, onPhotoChange }) => {
  const showExtra = !!(card.preferred_name || card.suffix || form.preferred_name || form.suffix);
  return (
    <div className="space-y-3" data-testid="pc-edit-main">
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Photograph</div>
        <PersonnelPhotoField personnelId={card.id} hasPhoto={!!card.photo_url} onChange={onPhotoChange} />
      </div>
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Name</div>
        <div className="grid grid-cols-12 gap-2">
          <EF form={form} setForm={setForm} k="legal_first_name" label="First name" cls="col-span-12 sm:col-span-4" />
          <EF form={form} setForm={setForm} k="legal_middle_name" label="Middle name" cls="col-span-12 sm:col-span-4" />
          <EF form={form} setForm={setForm} k="legal_last_name" label="Last name" cls="col-span-12 sm:col-span-4" />
          {showExtra && <>
            <EF form={form} setForm={setForm} k="preferred_name" label="Preferred name" cls="col-span-6 sm:col-span-4" />
            <EF form={form} setForm={setForm} k="suffix" label="Suffix" cls="col-span-6 sm:col-span-2" />
          </>}
        </div>
      </div>
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Telephone</div>
        <div className="grid grid-cols-12 gap-2">
          <EF form={form} setForm={setForm} k="phone" label="Cell phone" cls="col-span-12 sm:col-span-4" />
          <EF form={form} setForm={setForm} k="home_phone" label="Home phone" cls="col-span-12 sm:col-span-4" />
          <EF form={form} setForm={setForm} k="alt_phone" label="Alternate phone" cls="col-span-12 sm:col-span-4" />
        </div>
      </div>
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Contact</div>
        <EF form={form} setForm={setForm} k="email" label="Email" cls="block" />
      </div>
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Address</div>
        <div className="grid grid-cols-12 gap-2">
          <EF form={form} setForm={setForm} k="street_address" label="Street address" cls="col-span-12 sm:col-span-8" />
          <EF form={form} setForm={setForm} k="address_line2" label="Apt/Unit" cls="col-span-12 sm:col-span-4" />
          <EF form={form} setForm={setForm} k="city" label="City" cls="col-span-6 sm:col-span-6" />
          <EF form={form} setForm={setForm} k="state" label="State" cls="col-span-3 sm:col-span-2" />
          <EF form={form} setForm={setForm} k="zip_code" label="ZIP / ZIP+4" cls="col-span-3 sm:col-span-4" />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
        <label className="text-[10px] font-semibold uppercase text-slate-500">Administrative effective date
          <input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)}
            className="ml-2 rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="pc-edit-effdate" />
        </label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="pc-edit-cancel-main">Cancel</button>
          <button onClick={onSave} disabled={saving} className="rounded-lg bg-[#1763B6] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="pc-edit-save-main">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
      <p className="text-[10px] text-slate-400">Existing values stay unless you change them — a blank field never erases saved data. Actual hire, termination, assignment and document dates are never changed by this effective date.</p>
    </div>
  );
};

const ReportingEditForm = ({ people, selfId, form, setForm, effDate, setEffDate, onSave, onCancel, saving }) => {
  const options = (people || []).filter((p) => p.id !== selfId);
  const pick = (nameKey, idKey) => (e) => {
    const id = e.target.value;
    const person = options.find((p) => p.id === id);
    setForm((f) => ({ ...f, [idKey]: id, [nameKey]: person ? person.name : '' }));
  };
  const Sel = ({ nameKey, idKey, label }) => (
    <label className="block text-[10px] font-semibold uppercase text-slate-500">{label}
      <select value={form[idKey] || ''} onChange={pick(nameKey, idKey)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-xs font-normal text-slate-800" data-testid={`pc-reporting-${idKey}`}>
        <option value="">{form[nameKey] ? `${form[nameKey]} (current)` : '— None —'}</option>
        {options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select></label>
  );
  return (
    <div className="space-y-2" data-testid="pc-edit-reporting">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {Sel({ nameKey: 'direct_supervisor', idKey: 'direct_supervisor_id', label: 'Direct supervisor' })}
        {Sel({ nameKey: 'operations_manager', idKey: 'operations_manager_id', label: 'Operations manager' })}
        {Sel({ nameKey: 'reporting_manager', idKey: 'reporting_manager_id', label: 'Reporting manager' })}
      </div>
      <p className="text-[10px] text-slate-400">Linked to existing Personnel records. No automatic "reports to Operations Manager" — leave a selector as None where it genuinely does not apply (e.g. the owner).</p>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
        <label className="text-[10px] font-semibold uppercase text-slate-500">Effective date
          <input type="date" value={form.reporting_effective_date || ''} onChange={(e) => setForm((f) => ({ ...f, reporting_effective_date: e.target.value }))} className="ml-2 rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="pc-reporting-effdate" /></label>
        <div className="flex gap-2"><button onClick={onCancel} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="pc-edit-cancel-reporting">Cancel</button>
          <button onClick={onSave} disabled={saving} className="rounded-lg bg-[#1763B6] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="pc-edit-save-reporting">{saving ? 'Saving…' : 'Save'}</button></div>
      </div>
    </div>
  );
};

const InlineEditForm = ({ section, card, effDate, setEffDate, form, setForm, onSave, onCancel, saving }) => (
  <div className="space-y-3" data-testid={`pc-edit-${section}`}>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {SECTION_DEFS[section].filter(([, label]) => label).map(([k, label]) => {
        const isDate = /_date$|_expiration$/.test(k);
        return (
          <label key={k} className="block text-[10px] font-semibold uppercase text-slate-500">{isDate ? label.replace(/\s*\(YYYY-MM-DD\)/i, '') : label}
            <input type={isDate ? 'date' : 'text'} value={form[k] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-xs font-normal text-slate-800 focus:border-[#1763B6] outline-none" data-testid={`pc-edit-input-${k}`} />
          </label>
        );
      })}
    </div>
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
      <label className="text-[10px] font-semibold uppercase text-slate-500">Administrative effective date
        <input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} className="ml-2 rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid={`pc-edit-effdate-${section}`} /></label>
      <div className="flex gap-2">
        <button onClick={onCancel} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid={`pc-edit-cancel-${section}`}>Cancel</button>
        <button onClick={onSave} disabled={saving} className="rounded-lg bg-[#1763B6] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid={`pc-edit-save-${section}`}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
    <p className="text-[10px] text-slate-400">A blank field never erases a saved value unless you clear it on purpose. Dates are stored safely and shown as MM/DD/YY.</p>
  </div>
);
const SECTION_EDIT_END = true;


const Fld = ({ k, v, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <dt className="text-[10px] uppercase tracking-wide text-slate-400">{k}</dt>
    <dd className="font-medium text-slate-700">{v || '—'}</dd>
  </div>
);

const SignModal = ({ pid, doc, token, template, draft, classification, assignment, approved = true, onClose, onDone }) => {
  const hasTemplate = !!(template && (template.en || template.es));
  const [lang, setLang] = useState(draft?.language === 'Español' ? 'es' : 'en');
  const [method, setMethod] = useState(draft?.method || 'drawn');
  const [name, setName] = useState(draft?.typed_name || '');
  const [acknowledged, setAcknowledged] = useState(!!draft?.acknowledged);
  const [assisted, setAssisted] = useState(false);
  const [repName, setRepName] = useState(draft?.tfs_rep_name || '');
  const needBank = !!doc.collect_banking;
  const [bank, setBank] = useState(draft?.banking || {
    payee_name: '', bank_name: '', routing_number: '', account_number: '', account_type: 'Checking',
    allocation: 'Entire payment', specific_amount: '', remainder: 'Manual paper check',
    secondary: { bank_name: '', routing_number: '', account_number: '', account_type: 'Checking' },
  });
  const [vdoc, setVdoc] = useState(draft?.verification_doc_id ? { id: draft.verification_doc_id, name: 'On file' } : null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [verification, setVerification] = useState(draft?.banking?.verification || null);
  const [verifying, setVerifying] = useState(false);
  const setB = (k, v) => { dirtyRef.current = true; setBank((b) => ({ ...b, [k]: v })); };
  const setSec = (k, v) => { dirtyRef.current = true; setBank((b) => ({ ...b, secondary: { ...b.secondary, [k]: v } })); };
  const [busy, setBusy] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmExit, setConfirmExit] = useState(false);
  const dirtyRef = useRef(false);
  const empCanvas = useRef(null);
  const repCanvas = useRef(null);
  const empDrawing = useRef(false);
  const repDrawing = useRef(false);
  const needAck = doc.requires_acknowledgment || hasTemplate;
  const needRep = !!doc.requires_tfs_rep;

  const T = (en, es) => (lang === 'es' ? es : en);
  const chosen = hasTemplate ? (lang === 'es' ? (template.es || template.en) : (template.en || template.es)) : null;

  // restore a drawn draft signature once the canvas is mounted
  useEffect(() => {
    const load = (ref, data) => {
      if (!ref.current || !data) return;
      const img = new Image();
      img.onload = () => { try { ref.current.getContext('2d').drawImage(img, 0, 0, ref.current.width, ref.current.height); } catch (e) {} };
      img.src = data;
    };
    if (method === 'drawn' && draft?.signature_image) load(empCanvas, draft.signature_image);
    if (needRep && draft?.tfs_rep_signature_image) load(repCanvas, draft.tfs_rep_signature_image);
  }, [method]); // eslint-disable-line

  const pos = (c, e) => {
    const r = c.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - r.left) * (c.width / r.width), y: (cy - r.top) * (c.height / r.height) };
  };
  const drawTo = (ref, flag) => (e) => {
    if (!flag.current) return;
    const c = ref.current; const { x, y } = pos(c, e);
    const ctx = c.getContext('2d'); ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.strokeStyle = '#0E2A47';
    ctx.lineTo(x, y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x, y);
  };
  const startDraw = (ref, flag) => (e) => { flag.current = true; dirtyRef.current = true; drawTo(ref, flag)(e); };
  const endDraw = (ref, flag) => () => { flag.current = false; const c = ref.current; if (c) c.getContext('2d').beginPath(); };
  const clearPad = (ref) => { const c = ref.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); };
  const padEmpty = (ref) => {
    const c = ref.current; if (!c) return true;
    const d = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < d.length; i += 4) if (d[i] !== 0) return false;
    return true;
  };

  const markDirty = (fn) => (v) => { dirtyRef.current = true; fn(v); };
  const isDirty = () => dirtyRef.current || name.trim() || acknowledged || repName.trim();

  const uploadVoided = async (file) => {
    if (!file) return;
    setUploadingDoc(true); setErr(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/personnel/${pid}/documents?doc_key=bank_verification`, fd, auth(token));
      setVdoc({ id: r.data.document.id, name: file.name }); dirtyRef.current = true;
      toast.success(T('Bank verification document uploaded', 'Documento de verificación bancaria cargado'));
      runVerify(r.data.document.id);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not upload document'); }
    setUploadingDoc(false);
  };

  const runVerify = async (docId) => {
    const id = docId || vdoc?.id;
    if (!id) return;
    setVerifying(true); setVerification(null);
    try {
      const r = await axios.post(`${API}/personnel/${pid}/direct-deposit/verify-document`, {
        doc_id: id,
        banking: {
          payee_name: bank.payee_name.trim(), bank_name: bank.bank_name.trim(),
          routing_number: bank.routing_number.trim(), account_number: bank.account_number.trim(),
          account_type: bank.account_type,
        },
      }, auth(token));
      setVerification(r.data.verification); dirtyRef.current = true;
    } catch (e) { setErr(e.response?.data?.detail || 'Could not read the document'); }
    setVerifying(false);
  };

  const buildPayload = () => {
    const p = {
      doc_key: doc.key, method, typed_name: name.trim(),
      language: lang === 'es' ? 'Español' : 'English',
      acknowledged, actor_is_applicant: true,
      manager_assisted: assisted, assisted_by: assisted ? 'Manager' : null,
      tfs_rep_name: repName.trim() || null,
    };
    if (doc.per_assignment && assignment) p.assignment_id = assignment.assignment_id;
    if (needBank) {
      const spec = bank.allocation === 'Specific amount';
      p.banking = {
        payee_name: bank.payee_name.trim(), bank_name: bank.bank_name.trim(),
        routing_number: bank.routing_number.trim(), account_number: bank.account_number.trim(),
        account_type: bank.account_type, allocation: bank.allocation,
        specific_amount: spec ? Number(bank.specific_amount) : undefined,
        remainder: spec ? bank.remainder : undefined,
        secondary: (spec && bank.remainder === 'Secondary account') ? {
          bank_name: bank.secondary.bank_name.trim(), routing_number: bank.secondary.routing_number.trim(),
          account_number: bank.secondary.account_number.trim(), account_type: bank.secondary.account_type,
        } : undefined,
        verification_doc_id: vdoc?.id || undefined,
        verification: verification || undefined,
      };
    }
    if (method === 'drawn' && !padEmpty(empCanvas)) p.signature_image = empCanvas.current.toDataURL('image/png');
    if (needRep && !padEmpty(repCanvas)) p.tfs_rep_signature_image = repCanvas.current.toDataURL('image/png');
    return p;
  };

  const saveDraft = async () => {
    setSavingDraft(true); setErr(null);
    try {
      await axios.put(`${API}/personnel/${pid}/sign-draft`, buildPayload(), auth(token));
      toast.success(T('Draft saved', 'Borrador guardado'));
      dirtyRef.current = false; onDone(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not save draft'); }
    setSavingDraft(false);
  };

  const draftQs = doc.per_assignment && assignment ? `?assignment_id=${assignment.assignment_id}` : '';
  const submit = async () => {
    setErr(null);
    if (!approved) { setErr(T('This agreement is Pending Template Approval and cannot be signed yet.', 'Este acuerdo está Pendiente de Aprobación de Plantilla y aún no puede firmarse.')); return; }
    if (!name.trim()) { setErr(T('A typed full legal name is required.', 'Se requiere el nombre legal completo.')); return; }
    if (needAck && !acknowledged) { setErr(T('Please confirm you have read and agree to the agreement.', 'Confirme que ha leído y acepta el acuerdo.')); return; }
    if (needBank) {
      const digits = (s) => (s || '').replace(/\D/g, '');
      if (!bank.payee_name.trim() || !bank.bank_name.trim()) { setErr(T('Name on account and financial institution are required.', 'El nombre en la cuenta y la institución financiera son obligatorios.')); return; }
      if (digits(bank.routing_number).length < 9) { setErr(T('A valid 9-digit routing number is required.', 'Se requiere un número de ruta válido de 9 dígitos.')); return; }
      if (digits(bank.account_number).length < 4) { setErr(T('A valid account number is required.', 'Se requiere un número de cuenta válido.')); return; }
      if (bank.allocation === 'Specific amount') {
        if (!(Number(bank.specific_amount) > 0)) { setErr(T('Enter a specific deposit amount greater than $0.', 'Ingrese un monto de depósito mayor a $0.')); return; }
        if (bank.remainder === 'Secondary account' && (digits(bank.secondary.routing_number).length < 9 || digits(bank.secondary.account_number).length < 4 || !bank.secondary.bank_name.trim())) { setErr(T('Complete the secondary account details.', 'Complete los datos de la cuenta secundaria.')); return; }
      }
    }
    if (method === 'drawn' && padEmpty(empCanvas)) { setErr(T('Please draw your signature.', 'Dibuje su firma.')); return; }
    if (needRep && !repName.trim()) { setErr(T('A TFS representative name is required.', 'Se requiere el nombre del representante de TFS.')); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/personnel/${pid}/sign`, buildPayload(), auth(token));
      if (draft) { try { await axios.delete(`${API}/personnel/${pid}/sign-draft/${doc.key}${draftQs}`, auth(token)); } catch (e) {} }
      dirtyRef.current = false; toast.success(T('Agreement signed and saved', 'Acuerdo firmado y guardado')); onDone(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not record signature'); }
    setBusy(false);
  };

  const discardDraft = async () => {
    if (draft) { try { await axios.delete(`${API}/personnel/${pid}/sign-draft/${doc.key}${draftQs}`, auth(token)); onDone(); } catch (e) {} }
    onClose();
  };
  const attemptClose = () => { if (isDirty()) setConfirmExit(true); else onClose(); };

  const Canvas = (ref, flag, testid) => (
    <div>
      <canvas ref={ref} width={600} height={140}
        className="w-full touch-none rounded border border-slate-300 bg-slate-50"
        onMouseDown={startDraw(ref, flag)} onMouseMove={drawTo(ref, flag)} onMouseUp={endDraw(ref, flag)} onMouseLeave={endDraw(ref, flag)}
        onTouchStart={startDraw(ref, flag)} onTouchMove={drawTo(ref, flag)} onTouchEnd={endDraw(ref, flag)} data-testid={testid} />
      <button onClick={() => clearPad(ref)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500 underline" data-testid={`${testid}-clear`}><Eraser size={11} />{T('Clear signature', 'Borrar firma')}</button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3" data-testid="pc-sign-modal">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="text-base font-bold text-[#0E2A47]">{chosen?.title || doc.title}</div>
          <button onClick={attemptClose} data-testid="pc-sign-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {doc.needs_legal_review && !approved && (
            <div className="mb-3 rounded bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="pc-sign-pending">
              <div>Pending Template Approval — the official wording awaits Global Administrator approval. A binding signature is locked until it is approved.</div>
              {(doc.legal_flags || []).length > 0 && (
                <ul className="mt-1 list-disc pl-4" data-testid="pc-sign-legalflags">
                  {doc.legal_flags.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
            </div>
          )}
          {draft && <div className="mb-3 rounded bg-sky-50 px-3 py-2 text-[11px] text-sky-800" data-testid="pc-sign-draft-note">{T('A saved draft was loaded. You can continue where you left off.', 'Se cargó un borrador guardado. Puede continuar donde lo dejó.')}</div>}

          {doc.per_assignment && assignment && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="pc-sign-assignment">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{T('Assignment & compensation (from approved records)', 'Asignación y compensación (de registros aprobados)')}</div>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-[12px] text-slate-700 sm:grid-cols-2">
                <Fld k={T('Customer', 'Cliente')} v={assignment.customer} />
                <Fld k={T('Service location', 'Lugar de servicio')} v={assignment.service_location} />
                <Fld k={T('Address', 'Dirección')} v={assignment.service_address} full />
                <Fld k={T('Account / Location code', 'Cuenta / Código')} v={[assignment.account_number, assignment.location_code].filter(Boolean).join(' / ')} />
                <Fld k={T('Duties', 'Funciones')} v={assignment.duties} full />
                <Fld k={T('Compensation', 'Compensación')} v={assignment.compensation_approved ? money(assignment.compensation_amount) : T('Not yet approved', 'Aún no aprobada')} />
                <Fld k={T('Pay basis', 'Base de pago')} v={assignment.pay_basis} />
                <Fld k={T('Payment frequency', 'Frecuencia de pago')} v={assignment.pay_frequency} />
                <Fld k={T('Effective date', 'Fecha de vigencia')} v={assignment.effective_date_display} />
              </dl>
              {!assignment.compensation_approved && (
                <div className="mt-1.5 text-[11px] text-amber-700">{T('No approved compensation is on record for this assignment yet. A proposed rate is never shown here as approved.', 'Aún no hay compensación aprobada registrada para esta asignación. Una tarifa propuesta nunca se muestra aquí como aprobada.')}</div>
              )}
            </div>
          )}

          {hasTemplate && (
            <div className="mb-3 inline-flex overflow-hidden rounded-lg border border-slate-300" data-testid="pc-sign-lang">
              <button onClick={() => setLang('en')} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold ${lang === 'en' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`} data-testid="pc-sign-lang-en"><Languages size={12} />English</button>
              <button onClick={() => setLang('es')} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold ${lang === 'es' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`} data-testid="pc-sign-lang-es"><Languages size={12} />Español</button>
            </div>
          )}

          {chosen && (
            <div className="mb-4">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{T('Please review the full agreement', 'Revise el acuerdo completo')}</div>
              <div className="max-h-52 overflow-y-auto whitespace-pre-line rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] leading-relaxed text-slate-700" data-testid="pc-sign-body">{(chosen.body || '').replace('{name}', name.trim() || '______________________________')}</div>
              {template?.revision && <div className="mt-1 text-[10px] text-slate-400">{T('Revision', 'Revisión')} {template.revision}</div>}
            </div>
          )}

          {needBank && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="pc-sign-banking">
              <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500"><Landmark size={12} />{T('Banking information', 'Información bancaria')}</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input value={bank.payee_name} onChange={(e) => setB('payee_name', e.target.value)} placeholder={T('Name on bank account', 'Nombre en la cuenta')} className="rounded border border-slate-300 px-3 py-2 text-sm sm:col-span-2" data-testid="pc-bank-payee" />
                <input value={bank.bank_name} onChange={(e) => setB('bank_name', e.target.value)} placeholder={T('Financial institution', 'Institución financiera')} className="rounded border border-slate-300 px-3 py-2 text-sm sm:col-span-2" data-testid="pc-bank-name" />
                <input value={bank.routing_number} onChange={(e) => setB('routing_number', e.target.value)} placeholder={T('Routing number (9 digits)', 'Número de ruta (9 dígitos)')} inputMode="numeric" className="rounded border border-slate-300 px-3 py-2 text-sm" data-testid="pc-bank-routing" />
                <input value={bank.account_number} onChange={(e) => setB('account_number', e.target.value)} placeholder={T('Account number', 'Número de cuenta')} inputMode="numeric" className="rounded border border-slate-300 px-3 py-2 text-sm" data-testid="pc-bank-account" />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] text-slate-700">
                <span className="font-medium">{T('Account type', 'Tipo de cuenta')}:</span>
                {['Checking', 'Savings'].map((t) => (
                  <label key={t} className="flex items-center gap-1"><input type="radio" name="acct-type" checked={bank.account_type === t} onChange={() => setB('account_type', t)} data-testid={`pc-bank-type-${t.toLowerCase()}`} />{T(t, t === 'Checking' ? 'Corriente' : 'Ahorros')}</label>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] text-slate-700">
                <span className="font-medium">{T('Deposit', 'Depósito')}:</span>
                {['Entire payment', 'Specific amount'].map((a) => (
                  <label key={a} className="flex items-center gap-1"><input type="radio" name="alloc" checked={bank.allocation === a} onChange={() => setB('allocation', a)} data-testid={`pc-bank-alloc-${a === 'Entire payment' ? 'entire' : 'specific'}`} />{T(a, a === 'Entire payment' ? 'Pago completo' : 'Monto específico')}</label>
                ))}
              </div>
              {bank.allocation === 'Specific amount' && (
                <div className="mt-2 rounded border border-slate-200 bg-white p-2">
                  <input value={bank.specific_amount} onChange={(e) => setB('specific_amount', e.target.value)} placeholder={T('Specific amount to this account ($)', 'Monto específico a esta cuenta ($)')} inputMode="decimal" className="mb-2 w-full rounded border border-slate-300 px-3 py-2 text-sm" data-testid="pc-bank-specific" />
                  <div className="mb-2 text-[11px] font-medium text-slate-600">{T('Remaining balance paid by:', 'Saldo restante pagado por:')}</div>
                  <div className="flex flex-wrap gap-3 text-[12px] text-slate-700">
                    {['Manual paper check', 'Secondary account'].map((r) => (
                      <label key={r} className="flex items-center gap-1"><input type="radio" name="remainder" checked={bank.remainder === r} onChange={() => setB('remainder', r)} data-testid={`pc-bank-remainder-${r === 'Manual paper check' ? 'check' : 'secondary'}`} />{T(r, r === 'Manual paper check' ? 'Cheque en papel' : 'Cuenta secundaria')}</label>
                    ))}
                  </div>
                  {bank.remainder === 'Secondary account' && (
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="pc-bank-secondary">
                      <input value={bank.secondary.bank_name} onChange={(e) => setSec('bank_name', e.target.value)} placeholder={T('Secondary institution', 'Institución secundaria')} className="rounded border border-slate-300 px-3 py-2 text-sm sm:col-span-2" data-testid="pc-bank2-name" />
                      <input value={bank.secondary.routing_number} onChange={(e) => setSec('routing_number', e.target.value)} placeholder={T('Routing number', 'Número de ruta')} inputMode="numeric" className="rounded border border-slate-300 px-3 py-2 text-sm" data-testid="pc-bank2-routing" />
                      <input value={bank.secondary.account_number} onChange={(e) => setSec('account_number', e.target.value)} placeholder={T('Account number', 'Número de cuenta')} inputMode="numeric" className="rounded border border-slate-300 px-3 py-2 text-sm" data-testid="pc-bank2-account" />
                      <div className="flex flex-wrap items-center gap-3 text-[12px] text-slate-700 sm:col-span-2">
                        <span className="font-medium">{T('Type', 'Tipo')}:</span>
                        {['Checking', 'Savings'].map((t) => (
                          <label key={t} className="flex items-center gap-1"><input type="radio" name="acct2-type" checked={bank.secondary.account_type === t} onChange={() => setSec('account_type', t)} data-testid={`pc-bank2-type-${t.toLowerCase()}`} />{T(t, t === 'Checking' ? 'Corriente' : 'Ahorros')}</label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3 border-t border-slate-200 pt-2">
                <div className="mb-1 text-[11px] font-medium text-slate-600">{T('Voided check or bank verification', 'Cheque anulado o verificación bancaria')}</div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-[11px] font-semibold text-[#1763B6]"><Upload size={12} />{T('Upload file', 'Subir archivo')}<input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadVoided(e.target.files[0])} data-testid="pc-bank-upload" /></label>
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-[11px] font-semibold text-[#1763B6]"><Camera size={12} />{T('Take photo', 'Tomar foto')}<input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadVoided(e.target.files[0])} data-testid="pc-bank-camera" /></label>
                  {uploadingDoc && <Loader2 className="animate-spin text-slate-400" size={14} />}
                  {vdoc && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid="pc-bank-doc-ok"><CheckCircle2 size={13} />{vdoc.name}</span>}
                  {vdoc && <button type="button" onClick={() => runVerify()} disabled={verifying} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-600 disabled:opacity-60" data-testid="pc-bank-recheck">{verifying ? <Loader2 className="animate-spin" size={12} /> : null}{T('Re-check against entered info', 'Reverificar con lo ingresado')}</button>}
                </div>
                {verifying && <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500" data-testid="pc-bank-verifying"><Loader2 className="animate-spin" size={12} />{T('Reading the document…', 'Leyendo el documento…')}</div>}
                {verification && (() => {
                  const st = verification.overall_status;
                  const tone = st === 'Matched' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : st === 'Mismatch' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-800 border-amber-200';
                  const label = { 'Matched': T('Matched', 'Coincide'), 'Mismatch': T('Mismatch', 'No coincide'), 'Needs Verification': T('Needs Verification', 'Requiere verificación') }[st] || st;
                  return (
                    <div className={`mt-2 rounded-lg border p-2 ${tone}`} data-testid="pc-bank-verification">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-wide" data-testid="pc-bank-verify-status">{label}</span>
                        {vdoc && <a href={`${API}/personnel/${pid}/documents/${vdoc.id}/file?auth=${token}`} target="_blank" rel="noreferrer" className="text-[11px] underline" data-testid="pc-bank-view-doc">{T('View document', 'Ver documento')}</a>}
                      </div>
                      <table className="mt-1.5 w-full text-[11px]">
                        <thead><tr className="text-left text-slate-500"><th className="font-medium">{T('Field', 'Campo')}</th><th className="font-medium">{T('Entered', 'Ingresado')}</th><th className="font-medium">{T('From document', 'Del documento')}</th><th></th></tr></thead>
                        <tbody>
                          {(verification.fields || []).map((f) => (
                            <tr key={f.key} data-testid={`pc-bank-vfield-${f.key}`}>
                              <td className="pr-2 text-slate-600">{f.label}</td>
                              <td className="pr-2 text-slate-700">{f.entered || '—'}</td>
                              <td className="pr-2 text-slate-700">{f.readable ? (f.extracted || '—') : <span className="italic text-slate-400">{T('unreadable', 'ilegible')}</span>}</td>
                              <td>{f.match === true ? <CheckCircle2 size={13} className="text-emerald-600" /> : f.match === false ? <X size={13} className="text-rose-600" /> : <AlertCircle size={13} className="text-amber-500" />}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-1 text-[10px] opacity-80">{T('Automated reading is an aid only; it does not verify account ownership. An authorized reviewer confirms the result. Signing does not activate direct deposit.', 'La lectura automática es solo una ayuda; no verifica la titularidad de la cuenta. Un revisor autorizado confirma el resultado. Firmar no activa el depósito directo.')}</div>
                    </div>
                  );
                })()}
              </div>
              <div className="mt-2 text-[10px] text-slate-400">{T('Account numbers are encrypted and visible only to authorized payroll staff. Signing records the authorization — it does not activate direct deposit.', 'Los números de cuenta se cifran y solo son visibles para el personal de nómina autorizado. Firmar registra la autorización — no activa el depósito directo.')}</div>
            </div>
          )}

          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">{T('Typed legal name', 'Nombre legal escrito')}</label>
          <input value={name} onChange={(e) => { dirtyRef.current = true; setName(e.target.value); }} placeholder={T('Full legal name', 'Nombre legal completo')} className="mb-3 w-full rounded border border-slate-300 px-3 py-2 text-sm" data-testid="pc-sign-name" />

          <div className="mb-2 inline-flex overflow-hidden rounded-lg border border-slate-300">
            <button onClick={() => setMethod('typed')} className={`px-3 py-1.5 text-xs font-semibold ${method === 'typed' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`} data-testid="pc-sign-typed">{T('Type name', 'Escribir nombre')}</button>
            <button onClick={() => setMethod('drawn')} className={`px-3 py-1.5 text-xs font-semibold ${method === 'drawn' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`} data-testid="pc-sign-drawn">{T('Draw signature', 'Dibujar firma')}</button>
          </div>
          {method === 'drawn' && <div className="mb-3">{Canvas(empCanvas, empDrawing, 'pc-sign-canvas')}</div>}

          {needRep && (
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{T('Total Facility Solutions representative', 'Representante de Total Facility Solutions')}</div>
              <input value={repName} onChange={(e) => { dirtyRef.current = true; setRepName(e.target.value); }} placeholder={T('Representative full name', 'Nombre completo del representante')} className="mb-2 w-full rounded border border-slate-300 px-3 py-2 text-sm" data-testid="pc-sign-rep-name" />
              {Canvas(repCanvas, repDrawing, 'pc-sign-rep-canvas')}
            </div>
          )}

          {needAck && (
            <label className="mb-2 flex items-start gap-2 text-[12px] text-slate-700"><input type="checkbox" checked={acknowledged} onChange={(e) => { dirtyRef.current = true; setAcknowledged(e.target.checked); }} className="mt-0.5" data-testid="pc-sign-ack" />{T('I have read and agree to the agreement displayed above.', 'He leído y acepto el acuerdo mostrado arriba.')}</label>
          )}
          <label className="mb-3 flex items-center gap-2 text-[11px] text-slate-500"><input type="checkbox" checked={assisted} onChange={(e) => setAssisted(e.target.checked)} />{T('A manager assisted me with data entry.', 'Un gerente me ayudó con la entrada de datos.')}</label>

          {err && <div className="mb-2 rounded bg-rose-50 px-3 py-2 text-[11px] text-rose-600" data-testid="pc-sign-err">{err}</div>}
        </div>

        {confirmExit ? (
          <div className="border-t border-slate-100 bg-amber-50 px-5 py-3" data-testid="pc-sign-exit-confirm">
            <div className="mb-2 text-[12px] font-semibold text-amber-900">{T('You have unsaved changes. What would you like to do?', 'Tiene cambios sin guardar. ¿Qué desea hacer?')}</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={saveDraft} disabled={savingDraft} className="rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60" data-testid="pc-sign-exit-save">{T('Save Draft', 'Guardar borrador')}</button>
              <button onClick={discardDraft} className="rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-600" data-testid="pc-sign-exit-discard">{T('Discard', 'Descartar')}</button>
              <button onClick={() => setConfirmExit(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="pc-sign-exit-continue">{T('Continue Editing', 'Seguir editando')}</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-3">
            <button onClick={saveDraft} disabled={savingDraft || busy} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-60" data-testid="pc-sign-savedraft">{savingDraft ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}{T('Save Draft', 'Guardar borrador')}</button>
            <button onClick={submit} disabled={busy || savingDraft} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid="pc-sign-submit">{busy ? <Loader2 className="animate-spin" size={15} /> : <PenLine size={15} />}{T('Save Signed Document', 'Guardar documento firmado')}</button>
          </div>
        )}
      </div>
    </div>
  );
};

const money = (v) => (v == null ? '—' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const PayRow = ({ r, pending }) => (
  <tr className={pending ? 'bg-amber-50/40' : ''} data-testid={`ph-row-${r.id}`}>
    <td className="px-3 py-2 text-slate-700">{r.payment_date || '—'}</td>
    <td className="px-3 py-2 text-slate-600">{r.period_label || '—'}</td>
    <td className="px-3 py-2 text-slate-600 hidden sm:table-cell">{r.cadence_label || '—'}</td>
    <td className="px-3 py-2 text-slate-600 hidden md:table-cell">{[r.account_number, r.account_name].filter(Boolean).join(' · ') || '—'}</td>
    <td className="px-3 py-2 text-right font-semibold text-[#0E2A47] whitespace-nowrap">{money(r.gross)}</td>
    <td className="px-3 py-2">
      {pending
        ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">Pending Review</span>
        : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800"><Lock size={9} />Finalized</span>}
    </td>
  </tr>
);

const PayTable = ({ rows, pending }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200">
    <table className="w-full text-xs">
      <thead>
        <tr className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
          <th className="px-3 py-2">Payment Date</th>
          <th className="px-3 py-2">Service Period</th>
          <th className="px-3 py-2 hidden sm:table-cell">Pay Cycle</th>
          <th className="px-3 py-2 hidden md:table-cell">Account</th>
          <th className="px-3 py-2 text-right">Gross</th>
          <th className="px-3 py-2">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r) => <PayRow key={r.id} r={r} pending={pending} />)}
      </tbody>
    </table>
  </div>
);

const PaymentHistorySection = ({ personnelId, token }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [exp, setExp] = useState({ year: '', start: '', end: '' });
  const [dl, setDl] = useState('');
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading) {
      setLoading(true); setErr(null);
      try {
        const res = await axios.get(`${API}/payroll/personnel/${personnelId}/payment-history`, auth(token));
        setData(res.data);
      } catch (e) { setErr(e.response?.data?.detail || 'Could not load payment history'); }
      setLoading(false);
    }
  };
  const downloadStatement = async (format) => {
    setDl(format);
    try {
      const params = new URLSearchParams({ format });
      if (exp.year) params.set('year', exp.year);
      if (exp.start) params.set('start', exp.start);
      if (exp.end) params.set('end', exp.end);
      const res = await axios.get(`${API}/payroll/personnel/${personnelId}/payment-statement?${params.toString()}`, { ...auth(token), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `pay_statement.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not export the pay statement'); }
    setDl('');
  };
  const t = data?.totals || {};
  const years = Array.from(new Set([...(data?.finalized || []), ...(data?.pending_review || [])]
    .map((r) => String(r.payment_date_raw || r.year || '').slice(0, 4)).filter((y) => /^\d{4}$/.test(y)))).sort().reverse();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="pc-section-payment-history">
      <button onClick={toggle} className="flex w-full items-center justify-between" data-testid="ph-toggle">
        <div className="flex items-center gap-2 text-sm font-bold text-[#0E2A47]">
          <Receipt size={16} className="text-[#1763B6]" />Payment History
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">Read-only · Confidential</span>
        </div>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && (
        <div className="mt-3" data-testid="ph-body">
          {loading && <div className="flex items-center gap-2 py-6 text-sm text-slate-400" data-testid="ph-loading"><Loader2 className="animate-spin" size={15} />Loading payment history…</div>}
          {err && <div className="rounded bg-rose-50 px-3 py-2 text-xs text-rose-600" data-testid="ph-error">{err}</div>}
          {data && !loading && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg bg-emerald-50 px-3 py-2"><div className="text-[10px] font-semibold uppercase text-emerald-700">Finalized</div><div className="text-lg font-bold text-emerald-800" data-testid="ph-finalized-count">{t.finalized_count || 0}</div></div>
                <div className="rounded-lg bg-emerald-50 px-3 py-2"><div className="text-[10px] font-semibold uppercase text-emerald-700">Finalized Gross</div><div className="text-lg font-bold text-emerald-800">{money(t.finalized_gross)}</div></div>
                <div className="rounded-lg bg-amber-50 px-3 py-2"><div className="text-[10px] font-semibold uppercase text-amber-700">Pending Review</div><div className="text-lg font-bold text-amber-800" data-testid="ph-pending-count">{t.pending_count || 0}</div></div>
                <div className="rounded-lg bg-amber-50 px-3 py-2"><div className="text-[10px] font-semibold uppercase text-amber-700">Pending Gross</div><div className="text-lg font-bold text-amber-800">{money(t.pending_gross)}</div></div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="ph-export-toolbar">
                <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-slate-500"><FileText size={12} />Export Pay Statement</div>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col text-[10px] font-semibold uppercase text-slate-500">Year
                    <select value={exp.year} onChange={(e) => setExp({ year: e.target.value, start: '', end: '' })} className="mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs font-normal normal-case text-slate-700" data-testid="ph-export-year">
                      <option value="">All years</option>
                      {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </label>
                  <label className="flex flex-col text-[10px] font-semibold uppercase text-slate-500">From
                    <input type="date" value={exp.start} onChange={(e) => setExp((p) => ({ ...p, start: e.target.value, year: '' }))} className="mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="ph-export-start" />
                  </label>
                  <label className="flex flex-col text-[10px] font-semibold uppercase text-slate-500">To
                    <input type="date" value={exp.end} onChange={(e) => setExp((p) => ({ ...p, end: e.target.value, year: '' }))} className="mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="ph-export-end" />
                  </label>
                  <button onClick={() => downloadStatement('pdf')} disabled={!!dl} className="inline-flex items-center gap-1 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1763B6] disabled:opacity-50" data-testid="ph-export-pdf">
                    {dl === 'pdf' ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}PDF
                  </button>
                  <button onClick={() => downloadStatement('xlsx')} disabled={!!dl} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-100 disabled:opacity-50" data-testid="ph-export-xlsx">
                    {dl === 'xlsx' ? <Loader2 size={13} className="animate-spin" /> : <Receipt size={13} />}Excel
                  </button>
                </div>
                <p className="mt-2 text-[10px] text-slate-400">Only finalized payments are represented as officially paid. Pending Review items are clearly separated and never shown as paid.</p>
              </div>

              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-700"><Lock size={12} />Finalized / Official Payroll</div>
                {(data.finalized || []).length === 0
                  ? <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400" data-testid="ph-no-finalized">No finalized payments on record yet.</p>
                  : <PayTable rows={data.finalized} pending={false} />}
              </div>

              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-amber-700"><AlertCircle size={12} />Staged — Pending Review (not yet finalized)</div>
                {(data.pending_review || []).length === 0
                  ? <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400" data-testid="ph-no-pending">No staged pay runs awaiting review.</p>
                  : <>
                      <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="ph-pending-banner">
                        These records are staged for review and are <b>not</b> finalized or official. Amounts are provisional until a Global Administrator finalizes the import.
                      </div>
                      <PayTable rows={data.pending_review} pending />
                    </>}
              </div>
              <p className="text-[10px] text-slate-400">{data.note}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const EmployeeProfileCard = ({ personnelId, onClose, onHiringDecision }) => {
  const { token } = useContext(AuthContext);
  const [card, setCard] = useState(null);
  const [meta, setMeta] = useState(null);
  const [signDoc, setSignDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [decBusy, setDecBusy] = useState(false);
  const [decComments, setDecComments] = useState('');
  const [showDataSheet, setShowDataSheet] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [ssnFull, setSsnFull] = useState(null);
  const [editSec, setEditSec] = useState(null);
  const [form, setForm] = useState({});
  const [effDate, setEffDate] = useState('2026-11-01');
  const [savingSec, setSavingSec] = useState(false);
  const [editScreen, setEditScreen] = useState(false);
  const [initForm, setInitForm] = useState({});
  const [people, setPeople] = useState([]);
  const [payCtx, setPayCtx] = useState(null);
  const [signAsg, setSignAsg] = useState(null);
  const approveTemplate = async (key) => {
    const sd = (meta?.signable_docs || []).find((d) => d.key === key);
    if (!window.confirm(`Approve the official wording for "${sd?.title || key}"? This is a Global Administrator action that enables binding electronic signatures for this agreement across all personnel.`)) return;
    try {
      await axios.post(`${API}/personnel/agreements/${key}/approve-template`, { approved: true }, auth(token));
      toast.success('Template wording approved — signing is now enabled.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not approve wording'); }
  };
  const startEdit = (sec) => {
    const init = {};
    SECTION_DEFS[sec].forEach(([k]) => { init[k] = card[k] ?? ''; });
    setForm(init); setInitForm(init); setEditSec(sec); setEffDate('2026-11-01');
  };
  const saveSec = async (sec) => {
    setSavingSec(true);
    // Fields the user intentionally emptied (had a value, now blank) are allowed to clear.
    const clears = Object.keys(form).filter((k) => (initForm[k] ?? '') !== '' && (form[k] ?? '') === '');
    try {
      const r = await axios.patch(`${API}/personnel/${card.id}/inline-edit`, { section: sec, fields: form, clears, effective_date: effDate }, auth(token));
      toast.success(r.data.changes?.length ? `Saved ${r.data.changes.length} change(s)` : 'No changes');
      setEditSec(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setSavingSec(false);
  };
  const editBtn = (sec) => card?.can_manage ? (
    <button onClick={() => startEdit(sec)} data-testid={`pc-edit-btn-${sec}`}
      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-[#1763B6] hover:bg-blue-50"><Pencil size={12} /> Edit</button>
  ) : null;
  const revealSsn = async () => {
    try {
      const r = await axios.get(`${API}/personnel/${card.id}/ssn`, auth(token));
      setSsnFull(r.data.ssn || '—');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reveal SSN'); }
  };
  const fileRef = useRef(null); // eslint-disable-line no-unused-vars
  const load = async () => {
    const [c, m] = await Promise.all([
      axios.get(`${API}/personnel/${personnelId}/profile-card`, auth(token)),
      axios.get(`${API}/personnel/onboarding/meta`, auth(token)),
    ]);
    setCard(c.data); setMeta(m.data);
    const cls = c.data?.worker_classification;
    if (cls === 'Individual 1099 Contractor' || cls === 'Registered Business Subcontractor') {
      axios.get(`${API}/personnel/${personnelId}/pay-acknowledgment-context`, auth(token))
        .then((r) => setPayCtx(r.data)).catch(() => setPayCtx(null));
    } else { setPayCtx(null); }
    axios.get(`${API}/personnel?status_filter=active`, auth(token))
      .then((r) => setPeople((Array.isArray(r.data) ? r.data : (r.data.personnel || [])).map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [personnelId]);
  const submitApproval = async () => { setBusy(true); try { await axios.post(`${API}/personnel/${personnelId}/submit-for-approval`, {}, auth(token)); await load(); } catch (_) {} setBusy(false); };
  const doDecision = async (decision, extra = {}) => {
    setDecBusy(true);
    try {
      const r = await axios.post(`${API}/personnel/${personnelId}/approval`, { decision, comments: decComments, ...extra }, auth(token));
      toast.success(decision === 'approve' ? `Approved — permanent personnel #${r.data?.personnel_number} assigned` : `Application ${decision === 'return' ? 'returned for correction' : 'rejected'}`);
      setDecComments(''); await load();
      if (onHiringDecision) onHiringDecision(decision);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not record the decision'));
    }
    setDecBusy(false);
  };
  const decide = (decision) => {
    const rd = card.hiring_readiness;
    if (decision === 'approve') {
      if (rd && !rd.ready) {
        const reason = window.prompt(`This application has outstanding required items:\n\n• ${rd.outstanding.join('\n• ')}\n\nTo approve anyway, type a documented override reason (required):`);
        if (!reason || !reason.trim()) return;
        if (!window.confirm(`Approve ${card.name} and assign a permanent personnel number now?\n\nThis records a Global Administrator override of the outstanding items.`)) return;
        return doDecision('approve', { override: true, override_reason: reason.trim() });
      }
      if (!window.confirm(`Approve ${card.name} and assign a permanent personnel number now?`)) return;
      return doDecision('approve');
    }
    if (decision === 'return') {
      if (!window.confirm('Return this application to Operations for correction?')) return;
      return doDecision('return');
    }
    if (!window.confirm('Reject this application? This cannot allocate a personnel number.')) return;
    return doDecision('reject');
  };
  if (!card) return <div className="flex items-center justify-center py-20 text-slate-400" data-testid="pc-loading"><Loader2 className="animate-spin" /></div>;
  const dd = card.direct_deposit || {};
  return (
    <div data-testid="employee-profile-card" style={{ fontFamily: DIR_FONT }}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {onClose ? <button onClick={onClose} className="text-sm font-semibold text-[#1763B6]" data-testid="pc-back">← Back to directory</button> : <span />}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowIntake(true)} data-testid="pc-intake-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#1763B6] bg-white px-3.5 py-2 text-sm font-semibold text-[#1763B6] hover:bg-blue-50">
            <Upload size={15} /> Upload PDF &amp; Match Information
          </button>
          <button onClick={() => setShowDataSheet(true)} data-testid="pc-datasheet-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#15375e]">
            <Printer size={15} /> Print / Download Data Sheet
          </button>
        </div>
      </div>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-start">
        <div className="h-28 w-28 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
          {card.photo_url ? <img src={`${API}/personnel/${card.id}/photo?variant=card&auth=${token}`} alt="" className="h-full w-full object-cover" data-testid="pc-photo" />
            : <div className="flex h-full w-full items-center justify-center text-3xl font-bold text-slate-300">{(card.name || '?').slice(0, 1)}</div>}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="text-2xl font-extrabold text-[#0E2A47]" data-testid="pc-name">{card.name}</div>
            {card.can_manage && <button onClick={() => startEdit('main')} data-testid="pc-edit-btn-main" className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-[#1763B6] hover:bg-blue-50"><Pencil size={12} /> Edit</button>}
          </div>
          <div className="mt-1 space-y-0.5 text-sm text-slate-600">
            <div className="flex items-center gap-2"><Phone size={14} className="text-[#1763B6]" />{card.phone_display || fmtPhone(card.phone)}</div>
            <div className="flex items-center gap-2"><Briefcase size={14} className="text-[#1763B6]" /><span className="font-semibold">{card.class_display || card.worker_classification}</span>{card.primary_operational_role ? ` · ${card.primary_operational_role}` : ''}{card.company_name ? ` · ${card.company_name}` : ''}</div>
            <div className="flex items-center gap-2"><Calendar size={14} className="text-[#1763B6]" />Hired {card.hire_date_display || fmtDate(card.hire_date)}</div>
            <div className="flex items-center gap-2"><MapPin size={14} className="text-[#1763B6]" />{card.address || '—'}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:text-right">
          <div className="text-slate-400">Personnel #</div><div className="font-bold text-[#0E2A47]" data-testid="pc-number">{card.personnel_number || (card.application_id ? <span className="text-amber-700">{card.application_id} · Application</span> : '—')}</div>
          <div className="text-slate-400">Status</div><div><Pill s={card.lifecycle_status || card.status_label} /></div>
          <div className="text-slate-400">Pay frequency</div><div className="font-semibold" data-testid="pc-pay-frequency">{card.pay_frequency_summary || card.payroll_frequency || '—'}</div>
          <div className="text-slate-400">Date of Birth</div><div className="font-bold text-[#0E2A47]" data-testid="pc-dob">{card.date_of_birth_display || '—'}</div>
          {card.proposed_start_date_display && (<><div className="text-slate-400">Proposed start</div><div className="font-bold text-[#0E2A47]" data-testid="pc-proposed-start">{card.proposed_start_date_display}</div></>)}
          <div className="text-slate-400">Termination</div><div className="font-semibold" data-testid="pc-termination-date">{card.termination_date_display || '—'}</div>
        </div>
      </div>

      {/* Access suspension / termination banners */}
      {editSec === 'main' && (
        <div className="mb-4 rounded-2xl border border-[#1763B6]/30 bg-blue-50/40 p-4" data-testid="pc-edit-main-wrap">
          <div className="mb-2 text-sm font-bold text-[#0E2A47]">Edit Main Personnel Information</div>
          <MainEditForm card={card} effDate={effDate} setEffDate={setEffDate} form={form} setForm={setForm} onSave={() => saveSec('main')} onCancel={() => setEditSec(null)} saving={savingSec} onPhotoChange={load} />
        </div>
      )}
      {card.access_suspension?.active && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs font-semibold text-amber-800" data-testid="pc-suspension-banner">
          System/site access temporarily suspended — {card.access_suspension?.reason}. This is not a termination and does not affect payroll eligibility.
        </div>
      )}

      {/* Hiring action bar — only for personnel inside the New-Hire approval workflow */}
      {card.hiring?.status && (
      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="pc-hiring-bar">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500">Hiring approval is separate from access, payroll, assignment and onboarding completion.</span>
          {['Draft', 'Returned for Correction'].includes(card.hiring?.status) &&
            <button onClick={submitApproval} disabled={busy} className="ml-auto inline-flex items-center gap-1 rounded-lg bg-[#1763B6] px-3 py-1.5 text-xs font-semibold text-white" data-testid="pc-submit-approval"><Send size={13} />Submit for GA approval</button>}
          {card.hiring?.status === 'Pending Approval' && !card.can_approve_hiring && <span className="ml-auto text-xs font-semibold text-amber-700">Awaiting Global Administrator approval</span>}
          {card.hiring?.status === 'Approved' && <span className="ml-auto text-xs font-semibold text-emerald-700">Approved by {card.hiring?.decided_by}{card.personnel_number ? ` · #${card.personnel_number}` : ''}</span>}
        </div>

        {/* Global Administrator decision — available directly on the application (any pre-approval state) */}
        {card.can_approve_hiring && ['Draft', 'Returned for Correction', 'Pending Approval'].includes(card.hiring?.status) && (
          <div className="mt-3 rounded-lg border border-[#0E2A47]/15 bg-white p-3" data-testid="pc-ga-decision">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#0E2A47]"><BadgeCheck size={14} className="text-[#1763B6]" /> Global Administrator decision</div>
            {card.hiring_readiness && (
              <div className="mb-3" data-testid="pc-hiring-readiness">
                <div className={`mb-1.5 text-xs font-semibold ${card.hiring_readiness.ready ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {card.hiring_readiness.ready
                    ? 'All required hiring items are complete.'
                    : `${card.hiring_readiness.outstanding_count} required item(s) outstanding — approval is blocked unless overridden.`}
                </div>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {card.hiring_readiness.requirements.map((r) => (
                    <div key={r.key} className="flex items-center gap-1.5 text-[11px]" data-testid={`pc-req-${r.key}`}>
                      {r.status === 'complete'
                        ? <CheckCircle2 size={13} className="shrink-0 text-emerald-600" />
                        : r.status === 'unavailable'
                          ? <Clock size={13} className="shrink-0 text-slate-400" />
                          : <AlertCircle size={13} className="shrink-0 text-amber-500" />}
                      <span className={r.status === 'complete' ? 'text-slate-500' : r.status === 'unavailable' ? 'text-slate-400' : 'font-medium text-slate-800'}>
                        {r.label}{r.detail ? ` · ${r.detail}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <textarea value={decComments} onChange={(e) => setDecComments(e.target.value)} placeholder="Decision comments (optional)"
              className="mb-2 w-full rounded border border-slate-300 px-3 py-2 text-sm" data-testid="pc-decision-comments" />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => decide('approve')} disabled={decBusy}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50" data-testid="pc-decision-approve">
                {card.hiring_readiness && !card.hiring_readiness.ready ? 'Approve with override…' : 'Approve & assign number'}
              </button>
              <button onClick={() => decide('return')} disabled={decBusy}
                className="rounded-lg bg-amber-500 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50" data-testid="pc-decision-return">Return for correction</button>
              <button onClick={() => decide('reject')} disabled={decBusy}
                className="rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50" data-testid="pc-decision-reject">Reject</button>
            </div>
          </div>
        )}
      </div>
      )}

      {card.onboarding && card.onboarding.total > 0 && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="pc-onboarding">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold text-[#0E2A47]"><BadgeCheck size={16} className="text-[#1763B6]" />Onboarding Progress</div>
            <span className="text-xs font-semibold text-slate-500" data-testid="pc-onboarding-count">{card.onboarding.complete} of {card.onboarding.total} complete</span>
          </div>
          <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#8FC93A] transition-all" style={{ width: `${Math.round((card.onboarding.complete / card.onboarding.total) * 100)}%` }} /></div>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {card.onboarding.items.map((it) => (
              <div key={it.key} className="flex items-center gap-2 text-xs" data-testid={`pc-onboarding-${it.key}`}>
                {it.status === 'complete'
                  ? <CheckCircle2 size={14} className="shrink-0 text-emerald-600" />
                  : <AlertCircle size={14} className="shrink-0 text-amber-500" />}
                <span className={it.status === 'complete' ? 'text-slate-600' : 'font-medium text-slate-800'}>{it.label}{it.detail ? ` · ${it.detail}` : ''}</span>
                {it.status !== 'complete' && <span className="ml-auto text-[10px] font-semibold uppercase text-amber-600">Outstanding</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section icon={Phone} title="Emergency Contact" action={editBtn('emergency')}>
          {editSec === 'emergency' ? (
            <InlineEditForm section="emergency" card={card} effDate={effDate} setEffDate={setEffDate} form={form} setForm={setForm} onSave={() => saveSec('emergency')} onCancel={() => setEditSec(null)} saving={savingSec} />
          ) : (
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between"><span className="text-slate-500">Contact</span><span className="font-medium" data-testid="pc-ec-name">{card.emergency_contact_name || '—'}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Relationship</span><span>{card.emergency_contact_relationship || '—'}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Telephone</span><span>{card.emergency_contact_phone_display || fmtPhone(card.emergency_contact_phone)}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Alternate</span><span>{card.emergency_contact_alt_phone_display || fmtPhone(card.emergency_contact_alt_phone)}</span></div>
            {card.can_view_restricted && (card.medical_emergency_info || card.medical_accommodations) && (
              <div className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-800" data-testid="pc-medical">
                <div className="font-semibold">Restricted medical (authorized access)</div>
                {card.medical_emergency_info && <div>Emergency: {card.medical_emergency_info}</div>}
                {card.medical_accommodations && <div>Accommodations: {card.medical_accommodations}</div>}
              </div>)}
            {card.medical_restricted && <div className="text-[10px] text-slate-400">Restricted medical information on file — not shown at your access level.</div>}
          </div>
          )}
        </Section>

        <Section icon={MapPin} title="Active Assignments">
          {(card.assignments || []).length === 0 ? <p className="text-xs text-slate-400" data-testid="pc-no-assignment">No assignment currently recorded.</p>
            : card.assignments.map((a, i) => (
              <div key={i} className="border-t border-slate-100 py-1.5 text-sm first:border-0" data-testid="pc-assignment-row">
                <div className="font-medium">{a.customer || a.service_location || a.account_number || '—'}</div>
                <div className="text-[11px] text-slate-500">
                  {[a.service_location, a.location_code, a.role_code || a.role].filter(Boolean).join(' · ')} · Eff {a.effective_date_display || '—'}
                  {card.can_view_restricted && (a.pay_amount != null || a.pay_frequency) ? ` · ${a.pay_amount != null ? '$' + Number(a.pay_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}${a.pay_frequency ? ' ' + a.pay_frequency : ''}` : ''}
                </div>
              </div>))}
        </Section>

        <Section icon={BadgeCheck} title="Identification & Work Authorization" className="lg:col-span-2" action={editBtn('identification')}>
          {(card.expiration_alerts || []).length > 0 && (
            <div className="mb-3 space-y-1.5" data-testid="pc-expiration-alerts">
              {card.expiration_alerts.map((a) => (
                <div key={a.key} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold ${a.status === 'expired' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`} data-testid={`pc-exp-alert-${a.key}`}>
                  <AlertTriangle size={14} />
                  <span>{a.label} {a.status === 'expired' ? `expired on ${a.display} (${Math.abs(a.days_remaining)} day${Math.abs(a.days_remaining) === 1 ? '' : 's'} ago)` : `expires ${a.display} — ${a.days_remaining} day${a.days_remaining === 1 ? '' : 's'} left`}</span>
                </div>
              ))}
            </div>
          )}
          {editSec === 'identification' ? (
            <InlineEditForm section="identification" card={card} effDate={effDate} setEffDate={setEffDate} form={form} setForm={setForm} onSave={() => saveSec('identification')} onCancel={() => setEditSec(null)} saving={savingSec} />
          ) : (
          <div className="grid grid-cols-1 gap-x-10 gap-y-1.5 text-sm sm:grid-cols-2">
            <div className="flex items-center justify-between"><span className="text-slate-500">Photo ID</span><span>{card.gov_id_type || '—'} {card.gov_id_status ? <Pill s={card.gov_id_status} /> : null}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">ID number</span><span className="font-mono" data-testid="pc-gov-id-number">{card.gov_id_number || (card.gov_id_number_on_file ? 'On File' : '—')}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Issuing authority</span><span>{card.gov_id_issuing_authority || '—'}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">SSN</span><span className="flex items-center gap-2 font-mono" data-testid="pc-ssn">{ssnFull || card.ssn_last4_masked || '—'}{card.ssn_full_available && !ssnFull && <button onClick={revealSsn} className="font-sans text-[11px] font-semibold text-[#1763B6] underline" data-testid="pc-ssn-reveal">Reveal full</button>}{ssnFull && <button onClick={() => setSsnFull(null)} className="font-sans text-[11px] font-semibold text-slate-400 underline" data-testid="pc-ssn-hide">Hide</button>}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Work authorization</span><span>{[card.work_auth_status, card.work_auth_date_display].filter(Boolean).join(' · ') || '—'}</span></div>
            {card.work_auth_reverify_date_display && <div className="flex items-center justify-between"><span className="text-slate-500">Reverification due</span><span>{card.work_auth_reverify_date_display}</span></div>}
            <div className="flex items-center justify-between"><span className="text-slate-500">Driver's license</span><span>{card.dl_number ? <span className="font-mono">{card.dl_number}</span> : (card.dl_state || card.dl_expiration_display ? 'On File' : '—')}{card.dl_state ? ` · ${card.dl_state}` : ''}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">DL expiration</span><span className={card.license_expired_flag ? 'font-semibold text-rose-600' : ''}>{card.dl_expiration_display || '—'}{card.license_expired_flag ? ' (expired — review)' : ''}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Authorized company driver</span><Pill s={card.authorized_company_driver} /></div>
          </div>
          )}
          {editSec !== 'identification' && (
            <div className="mt-3 border-t border-slate-100 pt-3" data-testid="pc-identification-docs-block">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-bold text-[#0E2A47]"><IdCard size={14} className="text-[#1763B6]" />Identification Documents</div>
              <IdentityDocuments personnelId={personnelId} token={token} canManage={!!card.can_manage} dlExpiration={card.dl_expiration_display} dlExpired={card.license_expired_flag} dlState={card.dl_state} />
            </div>
          )}
        </Section>

        <Section icon={FileText} title="Documents on File" className="lg:col-span-2">
          <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            {Object.entries(card.documents || {}).map(([k, d]) => (
              <div key={k} className="flex items-center justify-between gap-3 border-t border-slate-100 py-2 text-sm first:border-0 sm:[&:nth-child(2)]:border-0">
                <span className="flex min-w-0 flex-1 items-center gap-1">
                  <span className="truncate" title={d.label}>{d.label}</span>
                  {d.restricted && <span className="shrink-0 text-[9px] text-slate-400">(restricted)</span>}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <Pill s={d.status} />
                  {card.can_manage && <DocumentCapture personnelId={personnelId} docKey={k} label={d.label} onDone={load} />}
                </span>
              </div>))}
          </div>
        </Section>

        <Section icon={FileText} title="Government Forms (W-4 / I-9 / W-9)" className="lg:col-span-2">
          <p className="mb-3 text-xs text-slate-500">Complete and e-sign the required federal forms for this applicant right here — no separate upload needed. These are the official IRS/USCIS forms and feed the hiring-approval checklist above.</p>
          <GovFormsPanel personnelId={personnelId} person={{ name: card.name, classification: card.worker_classification }} />
        </Section>

        <Section icon={PenLine} title="Signed Documents">
          {(meta?.signable_docs || []).filter((sd) => {
            const scope = sd.scope || 'all';
            return scope === 'all' || (card.worker_classification && scope.split(',').includes(card.worker_classification));
          }).map((sd) => {
            const approved = !sd.needs_legal_review || !!(meta?.template_approvals || {})[sd.key]?.approved;
            const canApprove = card.is_global_admin && sd.needs_legal_review && !approved && sd.has_template;

            // Per-assignment agreements (e.g. Acknowledgment of Pay) — one row per active assignment.
            if (sd.per_assignment) {
              const rows = payCtx?.assignments || [];
              return (
                <div key={sd.key} className="border-t border-slate-100 py-1.5 first:border-0" data-testid={`pc-signrow-${sd.key}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-[#0E2A47]">{sd.title}
                      {sd.has_template && <span className="ml-1 rounded bg-emerald-50 px-1 text-[9px] font-semibold text-emerald-700">EN · ES</span>}
                      {!approved && (sd.has_template
                        ? <span className="ml-1 rounded bg-amber-50 px-1 text-[9px] font-semibold text-amber-700" data-testid={`pc-pending-${sd.key}`}>Pending Template Approval</span>
                        : <span className="ml-1 rounded bg-slate-100 px-1 text-[9px] font-semibold text-slate-500" data-testid={`pc-awaiting-${sd.key}`}>Awaiting wording</span>)}
                    </span>
                    {canApprove && <button onClick={() => approveTemplate(sd.key)} className="rounded border border-emerald-300 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-50" data-testid={`pc-approve-${sd.key}`}>Approve wording (GA)</button>}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">One acknowledgment per active assignment. Compensation shown from approved records only.</div>
                  <div className="mt-1.5 space-y-1.5">
                    {rows.length === 0 && <div className="text-[11px] text-slate-400" data-testid={`pc-noasg-${sd.key}`}>No active assignments on record.</div>}
                    {rows.map((a) => (
                      <div key={a.assignment_id} className="flex items-center justify-between rounded border border-slate-100 bg-slate-50/60 px-2.5 py-1.5" data-testid={`pc-asgrow-${a.assignment_id}`}>
                        <div className="min-w-0 pr-2 text-[12px]">
                          <div className="truncate font-medium text-slate-700">{a.customer || '—'}{a.service_location ? ` · ${a.service_location}` : ''}</div>
                          <div className="truncate text-[11px] text-slate-500">{a.compensation_approved ? `${money(a.compensation_amount)} · ${a.pay_basis || ''} · ${a.pay_frequency || ''}` : 'Compensation not yet approved'}{a.effective_date_display ? ` · eff ${a.effective_date_display}` : ''}</div>
                        </div>
                        <span className="flex shrink-0 items-center gap-2">
                          {a.signed && <><Pill s="Signed" /><a href={`${API}/personnel/${card.id}/signed/${a.signed_id}/pdf?auth=${token}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#1763B6] underline" data-testid={`pc-asg-pdf-${a.assignment_id}`}>PDF</a></>}
                          {approved
                            ? <button onClick={() => { setSignAsg(a); setSignDoc(sd); }} className="text-[11px] text-[#1763B6] underline" data-testid={`pc-asg-sign-${a.assignment_id}`}>{a.signed ? 're-sign' : 'sign'}</button>
                            : <span className="text-[11px] text-slate-400">locked</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>);
            }

            const signed = (card.signed_documents || []).filter((s) => s.doc_key === sd.key);
            const last = signed[signed.length - 1];
            const hasDraft = (card.signed_doc_drafts || []).some((d) => d.doc_key === sd.key);
            return (
              <div key={sd.key} className="border-t border-slate-100 py-1.5 text-sm first:border-0" data-testid={`pc-signrow-${sd.key}`}>
                <div className="flex items-center justify-between">
                  <span>{sd.title}
                    {!approved && (sd.has_template
                      ? <span className="ml-1 rounded bg-amber-50 px-1 text-[9px] font-semibold text-amber-700" data-testid={`pc-pending-${sd.key}`}>Pending Template Approval</span>
                      : <span className="ml-1 rounded bg-slate-100 px-1 text-[9px] font-semibold text-slate-500" data-testid={`pc-awaiting-${sd.key}`}>Awaiting wording</span>)}
                    {sd.has_template && <span className="ml-1 rounded bg-emerald-50 px-1 text-[9px] font-semibold text-emerald-700">EN · ES</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    {last && <><Pill s="Signed" /><a href={`${API}/personnel/${card.id}/signed/${last.id}/pdf?auth=${token}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#1763B6] underline" data-testid={`pc-signed-pdf-${sd.key}`}>PDF</a></>}
                    {!last && hasDraft && <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[9px] font-semibold text-sky-700" data-testid={`pc-draft-badge-${sd.key}`}>Draft saved</span>}
                    {canApprove && <button onClick={() => approveTemplate(sd.key)} className="rounded border border-emerald-300 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-50" data-testid={`pc-approve-${sd.key}`}>Approve wording (GA)</button>}
                    {approved
                      ? <button onClick={() => { setSignAsg(null); setSignDoc(sd); }} className="text-[11px] text-[#1763B6] underline" data-testid={`pc-sign-${sd.key}`}>{last ? 're-sign' : hasDraft ? 'resume' : 'sign'}</button>
                      : <span className="text-[11px] text-slate-400">locked</span>}
                  </span>
                </div>
                {!approved && (sd.legal_flags || []).length > 0 && (
                  <div className="mt-1 rounded bg-amber-50/70 px-2 py-1 text-[10px] text-amber-800" data-testid={`pc-legalflags-${sd.key}`}>
                    <span className="font-semibold">Flagged for legal review: </span>
                    <ul className="ml-3 list-disc">{sd.legal_flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
                  </div>
                )}
              </div>);
          })}
          {(() => {
            // Previously-signed RETIRED acknowledgments (e.g. legacy Company Rules &
            // Regulations) — kept read-only for audit; never offered for new signatures.
            const shownKeys = new Set((meta?.signable_docs || []).map((d) => d.key));
            const retiredSigned = (card.signed_documents || []).filter((s) => !shownKeys.has(s.doc_key));
            if (retiredSigned.length === 0) return null;
            const byKey = {};
            retiredSigned.forEach((s) => { (byKey[s.doc_key] = byKey[s.doc_key] || []).push(s); });
            return Object.entries(byKey).map(([key, list]) => {
              const latest = list[list.length - 1];
              return (
                <div key={key} className="border-t border-slate-100 py-1.5 text-sm first:border-0" data-testid={`pc-signrow-retired-${key}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">{latest.doc_title || key}
                      <span className="ml-1 rounded bg-slate-100 px-1 text-[9px] font-semibold text-slate-500" data-testid={`pc-retired-${key}`}>Retired · superseded by Company Guidelines</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Pill s="Signed" />
                      <a href={`${API}/personnel/${card.id}/signed/${latest.id}/pdf?auth=${token}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#1763B6] underline" data-testid={`pc-signed-pdf-retired-${key}`}>PDF</a>
                    </span>
                  </div>
                  <div className="mt-0.5 text-[10px] text-slate-400">Retained for audit only — no longer offered for new signatures.</div>
                </div>);
            });
          })()}
        </Section>

        <Section icon={BadgeCheck} title="Training & Certifications">
          <TrainingEditor card={card} token={token} onDone={load} />
          <div className="mt-2 text-[11px] text-slate-400">{card.training_summary?.completed} of {card.training_summary?.total} complete</div>
        </Section>

        <Section icon={ShieldCheck} title="Screening & Compliance" action={card.can_manage ? (
          <button onClick={() => setEditScreen((v) => !v)} data-testid="pc-edit-btn-screening" className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-[#1763B6] hover:bg-blue-50"><Pencil size={12} /> {editScreen ? 'Close' : 'Edit'}</button>) : null}>
          {editScreen ? <ScreeningEditor card={card} token={token} onDone={load} onClose={() => setEditScreen(false)} /> : (() => { const s = card.screening || {}; return (
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between"><span>Background check</span><Pill s={s.background_status || (s.background_required ? 'Pending' : 'Not Required')} /></div>
              {card.worker_classification === 'W-2 Employee' ? (
                <div className="flex items-center justify-between"><span>Workers' comp</span><span className="text-[12px] text-emerald-700">{s.workers_comp_status || 'Provided by TFS'}</span></div>
              ) : (<>
                <div className="flex items-center justify-between"><span>Workers' comp</span><Pill s={s.workers_comp_status || 'Coverage Review Required'} /></div>
                <div className="flex items-center justify-between"><span>Insurance</span><Pill s={s.insurance_status || 'Verification Required'} /></div>
                <div className="flex items-center justify-between"><span>Business verification</span><Pill s={s.business_verification_status || 'Pending'} /></div>
              </>)}
            </div>); })()}
        </Section>

        <Section icon={DollarSign} title="Payroll & Payment Method">
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between"><span>Payroll eligible</span><Pill s={card.payroll_eligible ? 'Active' : 'Not Applicable'} /></div>
            <div className="flex items-center justify-between"><span>Pay frequency</span><span className="font-medium">{card.pay_frequency_summary || card.payroll_frequency || '—'}</span></div>
            <div className="flex items-center justify-between"><span>Payment type</span><span className="font-medium" data-testid="pc-payment-type">{card.payment_type || 'Pending Setup'}</span></div>
            <div className="flex items-center justify-between"><span>Direct deposit</span><Pill s={dd.status} /></div>
            {dd.bank_name && <div className="flex items-center justify-between text-[12px] text-slate-500"><span>{dd.bank_name} · {dd.account_type}</span><span>••••{dd.account_last4 || '••••'}</span></div>}
            <div className="text-[10px] text-slate-400">Full bank numbers are never displayed in Operations.</div>
          </div>
        </Section>

        <PropertyRegisterSection personnelId={card.id} />

        <Section icon={Clock} title="Personnel Change History">
          <div className="space-y-2">
            {(card.status_history || []).slice(0, 8).map((h, i) => (
              <div key={i} className="flex gap-2 text-[12px]"><div className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1763B6]" /><div><span className="font-medium text-[#0E2A47]">{((h.action || h.status || 'Update').replace(/_/g, ' ')).replace(/\b\w/g, (c) => c.toUpperCase())}</span>{h.doc ? ` — ${h.doc}` : ''}{h.reason ? ` — ${h.reason}` : ''}<div className="text-[10px] text-slate-400">{(h.at || '').slice(0, 10)} · {h.by_name || h.by || ''}</div></div></div>))}
            {(card.status_history || []).length === 0 && <p className="text-xs text-slate-400">No history yet.</p>}
          </div>
        </Section>

        <Section icon={Briefcase} title="Reporting Relationships" className="lg:col-span-2" action={editBtn('reporting')}>
          {editSec === 'reporting' ? (
            <ReportingEditForm people={people} selfId={card.id} effDate={effDate} setEffDate={setEffDate} form={form} setForm={setForm} onSave={() => saveSec('reporting')} onCancel={() => setEditSec(null)} saving={savingSec} />
          ) : (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-4">
            <div><div className="text-[10px] font-semibold uppercase text-slate-400">Direct supervisor</div><div className="font-medium text-slate-700">{card.direct_supervisor || '—'}</div></div>
            <div><div className="text-[10px] font-semibold uppercase text-slate-400">Operations manager</div><div className="text-slate-700">{card.operations_manager || '—'}</div></div>
            <div><div className="text-[10px] font-semibold uppercase text-slate-400">Reporting manager</div><div className="text-slate-700">{card.reporting_manager || '—'}</div></div>
            <div><div className="text-[10px] font-semibold uppercase text-slate-400">Effective</div><div className="text-slate-700">{card.reporting_effective_date_display || '—'}</div></div>
          </div>
          )}
        </Section>
        {card.can_view_restricted && card.attendance_review && (
          <div className={`rounded-xl border p-3 text-sm shadow-sm ${card.attendance_review.review_due ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-white'}`} data-testid="pc-attendance-review">
            <div className="flex items-center gap-2 font-bold text-[#0E2A47]">
              <AlertCircle size={15} className={card.attendance_review.review_due ? 'text-rose-600' : 'text-slate-400'} />
              Attendance Review
            </div>
            <p className={`mt-1 text-[12px] ${card.attendance_review.review_due ? 'text-rose-700' : 'text-slate-500'}`} data-testid="pc-attendance-review-text">
              {card.attendance_review.unreported_absences} unreported absence(s) on record · review threshold {card.attendance_review.threshold}.
              {card.attendance_review.review_due ? ' Threshold reached — subject to management review.' : ''}
            </p>
          </div>
        )}
        {card.can_view_restricted && <PersonnelActions card={card} token={token} onDone={load} />}
      </div>

      {card.can_view_restricted && <div className="mt-4"><PaymentHistorySection personnelId={card.id} token={token} /></div>}

      {signDoc && <SignModal pid={card.id} doc={signDoc} token={token}
        template={(meta?.agreement_templates || {})[signDoc.key]}
        draft={signAsg
          ? (card.signed_doc_drafts || []).find((d) => d.doc_key === signDoc.key && d.assignment_id === signAsg.assignment_id)
          : (card.signed_doc_drafts || []).find((d) => d.doc_key === signDoc.key && !d.assignment_id)}
        classification={card.worker_classification}
        assignment={signAsg}
        approved={!signDoc.needs_legal_review || !!(meta?.template_approvals || {})[signDoc.key]?.approved}
        onClose={() => { setSignDoc(null); setSignAsg(null); }} onDone={load} />}
      {showDataSheet && <DataSheetViewer personnelId={card.id} name={card.name} onClose={() => setShowDataSheet(false)} />}
      {showIntake && <PersonnelIntake personnelId={card.id} personnelName={card.name} defaultClassification={card.worker_classification} onClose={() => setShowIntake(false)} />}
    </div>
  );
};

export default EmployeeProfileCard;
